import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, Banknote, CreditCard, X } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { createOrder, formatINR, validateCoupon, fetchPaymentConfig, initiatePayment, verifyPayment } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { PaisleyDivider, Lotus, Mandala } from "@/lib/motifs";
import { toast } from "sonner";

const Checkout = () => {
    const { cart: shopCart, cartSubtotal: shopSubtotal, clearCart: clearShopCart, buyNowItem, clearBuyNow, removeFromCart } = useShop();
    const routeLocation = useLocation();
    const wantsBuyNow = new URLSearchParams(routeLocation.search).get("buynow") === "1";
    const cart = wantsBuyNow ? (buyNowItem ? [buyNowItem] : []) : shopCart;
    const cartSubtotal = wantsBuyNow ? (buyNowItem ? buyNowItem.price * buyNowItem.quantity : 0) : shopSubtotal;
    const clearCart = wantsBuyNow ? clearBuyNow : clearShopCart;
    const { customer, authHeader } = useCustomerAuth();
    const nav = useNavigate();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(null);
    const [couponInput, setCouponInput] = useState("");
    const [couponApplied, setCouponApplied] = useState(null); // { code, discount_percent }
    const [couponError, setCouponError] = useState("");
    const [applyingCoupon, setApplyingCoupon] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("cod"); // "cod" | "online"
    const [onlineEnabled, setOnlineEnabled] = useState(false);
    const [codFee, setCodFee] = useState(50);
    const [pendingOrder, setPendingOrder] = useState(null); // online order ban gaya, payment baaki hai

    useEffect(() => {
        fetchPaymentConfig()
            .then((c) => {
                setOnlineEnabled(!!c.online_enabled);
                if (typeof c.cod_fee === "number") setCodFee(c.cod_fee);
            })
            .catch(() => setOnlineEnabled(false));
    }, []);
    const [form, setForm] = useState({
        full_name: "", email: "", phone: "",
        address_line1: "", address_line2: "", city: "", state: "", pincode: "",
    });

    useEffect(() => {
        if (customer === false) {
            nav(`/login?redirect=${encodeURIComponent("/checkout" + routeLocation.search)}`);
        } else if (customer) {
            const lastAddress = customer.addresses?.[customer.addresses.length - 1];
            setForm((p) => ({
                ...p,
                full_name: customer.name || p.full_name,
                email: customer.email || p.email,
                phone: customer.phone || p.phone,
                ...(lastAddress ? {
                    address_line1: lastAddress.address_line1,
                    address_line2: lastAddress.address_line2 || "",
                    city: lastAddress.city,
                    state: lastAddress.state,
                    pincode: lastAddress.pincode,
                } : {}),
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer]);

    const shipping = 0;
    const discountAmount = couponApplied ? Math.round((cartSubtotal * couponApplied.discount_percent) / 100) : 0;
    const codCharge = paymentMethod === "cod" ? codFee : 0;
    const total = cartSubtotal - discountAmount + shipping + codCharge;

    const change = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const applyCoupon = async () => {
        if (!couponInput.trim()) return;
        setApplyingCoupon(true);
        setCouponError("");
        try {
            const data = await validateCoupon(couponInput.trim(), form.email);
            setCouponApplied(data);
            toast.success(`Coupon applied — ${data.discount_percent}% off`);
        } catch (err) {
            setCouponApplied(null);
            setCouponError(err?.response?.data?.detail || "Invalid coupon code");
        } finally {
            setApplyingCoupon(false);
        }
    };

    const removeCoupon = () => {
        setCouponApplied(null);
        setCouponInput("");
        setCouponError("");
    };

    // [PAYMENT_GATEWAY_FRONTEND]
    // Gateway ka popup/redirect code yahan aayega.
    // `init` = backend ke gateway_create_payment() ka response.
    // Success par return karo: { data: <gateway ka response> }
    // User cancel kare to: throw { cancelled: true }
    const startOnlinePayment = async (order, init) => {
        return new Promise((resolve, reject) => {
            if (!window.EasebuzzCheckout) {
                reject(new Error("Payment SDK could not load"));
                return;
            }
            const checkout = new window.EasebuzzCheckout(init.merchant_key, init.env);
            checkout.initiatePayment({
                access_key: init.access_key,
                theme: "#7B1E2B",
                onResponse: (response) => {
                    const s = String(response?.status || "").toLowerCase();
                    if (s === "success") resolve({ data: response });
                    else if (s.includes("cancel")) reject({ cancelled: true });
                    else reject({ response: { data: { detail: "Payment failed. Please try again." } } });
                },
            });
        });
    };

    const payOnline = async (order) => {
        setLoading(true);
        try {
            const init = await initiatePayment(order.order_number, order.email);
            const result = await startOnlinePayment(order, init);
            const paidOrder = await verifyPayment(order.order_number, order.email, result?.data || {});
            setSuccess(paidOrder);
            setPendingOrder(null);
            clearCart();
            toast.success("Payment successful — order confirmed!");
        } catch (err) {
            if (err?.cancelled) {
                toast.error("Payment cancelled. You can retry whenever you're ready.");
            } else {
                const detail = err?.response?.data?.detail;
                toast.error(typeof detail === "string" ? detail : "Payment could not be completed. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (cart.length === 0) { toast.error("Your cart is empty"); return; }
        if (pendingOrder) { await payOnline(pendingOrder); return; }
        setLoading(true);
        try {
            const payload = {
                ...form,
                items: cart.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, size: i.size, image: i.image })),
                subtotal: cartSubtotal, shipping, total,
                coupon_code: couponApplied?.code || null,
                payment_method: paymentMethod,
                discount: discountAmount,
            };
            const order = await createOrder(payload, authHeader());
            if (paymentMethod === "online") {
                setPendingOrder(order);
                await payOnline(order);
                return;
            }
            if (paymentMethod === "online") {
                setPendingOrder(order);
                await payOnline(order);
                return;
            }
            setSuccess(order);
            clearCart();
            toast.success("Order confirmed!");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (Array.isArray(detail)) {
                // FastAPI validation errors come as a list of { loc, msg } objects
                const messages = detail.map((d) => {
                    const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "";
                    return field ? `${field}: ${d.msg}` : d.msg;
                });
                toast.error(messages.join(" · "));
            } else if (typeof detail === "string") {
                toast.error(detail);
            } else if (err?.request && !err?.response) {
                toast.error("Could not reach the server. Check your internet connection and try again.");
            } else {
                toast.error("Something went wrong. Please check your details and try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    if (!customer) {
        return <div className="container-x py-24 text-center font-body text-[color:var(--ink-muted)]">Loading…</div>;
    }

    if (cart.length === 0 && !success) {
        return (
            <div className="container-x py-24 text-center">
                <p className="font-heading text-2xl text-[color:var(--maroon)]">Your cart is empty</p>
                <button onClick={() => nav("/shop")} className="btn-primary mt-6">Browse Collection</button>
            </div>
        );
    }

    if (success) {
        return (
            <div className="container-x py-20 max-w-2xl mx-auto text-center relative" data-testid="checkout-success">
                <Mandala className="absolute -top-10 -left-16 opacity-15 slow-spin" size={260} />
                <Mandala className="absolute -bottom-10 -right-16 opacity-15" size={260} />
                <div className="relative">
                    <CheckCircle2 size={64} strokeWidth={1.2} className="mx-auto text-[color:var(--emerald)]" />
                    <p className="font-devanagari text-[color:var(--gold)] text-base mt-6">धन्यवाद!</p>
                    <h1 className="font-heading text-4xl md:text-5xl text-[color:var(--maroon)] mt-2">Thank you</h1>
                    <PaisleyDivider className="mt-4" />
                    <p className="font-body text-[color:var(--ink)] mt-6 leading-relaxed">
                        Your order has been received and is being lovingly prepared by our artisans. {success.payment_method === "online"
                            ? <>A confirmation has been sent to <span className="text-[color:var(--maroon)]">{success.email}</span>.</>
                            : <>We'll email <span className="text-[color:var(--maroon)]">{success.email}</span> as soon as our team confirms your order.</>}
                    </p>
                    <div className="mt-8 bg-white border border-[color:var(--border-gold)] p-6 inline-block">
                        <p className="font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--copper)]">Order Number</p>
                        <p className="font-heading text-3xl text-[color:var(--maroon)] mt-1" data-testid="order-number">{success.order_number}</p>
                    </div>
                    <div className="flex gap-3 justify-center mt-8 flex-wrap">
                        <button onClick={() => nav(`/track/${success.order_number}`)} className="btn-primary" data-testid="track-order-cta">Track Order</button>
                        <button onClick={() => nav("/shop")} className="btn-secondary">Continue Shopping</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-x py-14" data-testid="checkout-page">
            <div className="text-center mb-12">
                <Lotus className="mx-auto mb-3" />
                <p className="font-devanagari text-[color:var(--gold)] text-sm">विक्रय पूर्ण करें</p>
                <h1 className="font-heading text-5xl text-[color:var(--maroon)] mt-1">Checkout</h1>
                <PaisleyDivider className="mt-4" />
            </div>

            <form onSubmit={submit} className="grid lg:grid-cols-[1fr_400px] gap-12" data-testid="checkout-form">
                <div className="space-y-10">
                    <section>
                        <h2 className="font-heading text-2xl text-[color:var(--maroon)] mb-6">Shipping Details</h2>
                        <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                            {[
                                ["Full Name", "full_name", "text", true, "md:col-span-2"],
                                ["Email", "email", "email", true, ""],
                                ["Phone", "phone", "tel", true, ""],
                                ["Address Line 1", "address_line1", "text", true, "md:col-span-2"],
                                ["Address Line 2 (optional)", "address_line2", "text", false, "md:col-span-2"],
                                ["City", "city", "text", true, ""],
                                ["State", "state", "text", true, ""],
                                ["PIN Code", "pincode", "text", true, ""],
                            ].map(([label, key, type, req, cls]) => (
                                <div key={key} className={cls}>
                                    <label className="font-ui text-[10px] tracking-widest-x uppercase text-[color:var(--copper)] block mb-1">{label}</label>
                                    <input
                                        type={type}
                                        required={req}
                                        value={form[key]}
                                        onChange={(e) => change(key, e.target.value)}
                                        className="field"
                                        data-testid={`checkout-${key}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </section>

                    <section>
                        <h2 className="font-heading text-2xl text-[color:var(--maroon)] mb-6">Payment</h2>
                        <div className="space-y-3" data-testid="payment-methods">
                            {[
                                { key: "cod", title: "Cash on Delivery", desc: `Pay in cash on delivery. A ₹${codFee} COD charge applies.`, Icon: Banknote, disabled: false },
                                { key: "online", title: "Pay Online", desc: onlineEnabled ? "UPI, Cards, Net Banking & Wallets — secure payment." : "Currently unavailable.", Icon: CreditCard, disabled: !onlineEnabled },
                            ].map(({ key, title, desc, Icon, disabled }) => {
                                const active = paymentMethod === key;
                                const off = disabled || (!!pendingOrder && key !== "online");
                                return (
                                    <label
                                        key={key}
                                        className={`flex items-start gap-4 p-5 border transition-colors duration-300 ${
                                            active ? "border-[color:var(--maroon)] bg-[color:var(--ivory-2)]" : "border-[color:var(--border-gold)] bg-white"
                                        } ${off ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                                    >
                                        <input
                                            type="radio"
                                            name="payment_method"
                                            value={key}
                                            checked={active}
                                            disabled={off}
                                            onChange={() => setPaymentMethod(key)}
                                            className="mt-1 accent-[color:var(--maroon)]"
                                            data-testid={`payment-${key}`}
                                        />
                                        <Icon size={22} strokeWidth={1.4} className="text-[color:var(--gold)] mt-0.5 shrink-0" />
                                        <div>
                                            <p className="font-heading text-lg text-[color:var(--maroon)]">{title}</p>
                                            <p className="font-body text-sm text-[color:var(--ink-muted)] mt-0.5">{desc}</p>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                        {pendingOrder && (
                            <p className="font-body text-sm text-[color:var(--maroon)] mt-4">
                                Order <span className="font-semibold">{pendingOrder.order_number}</span> created — payment pending. Click "Retry Payment" to complete it.
                            </p>
                        )}
                    </section>
                </div>

                <aside className="lg:sticky lg:top-32 self-start bg-white border border-[color:var(--border-gold)] p-8">
                    <p className="font-ui text-xs tracking-widest-x uppercase text-[color:var(--copper)]">Order Summary</p>
                    <h3 className="font-heading text-2xl text-[color:var(--maroon)] mt-1">Your Kanganya order</h3>
                    <div className="mt-5 mb-1">
                        {couponApplied ? (
                            <div className="flex items-center justify-between bg-[color:var(--emerald)]/10 border border-[color:var(--emerald)]/40 px-3 py-2.5">
                                <span className="font-ui text-[11px] tracking-widest-x uppercase text-[color:var(--emerald)]">
                                    {couponApplied.code} applied — {couponApplied.discount_percent}% off
                                </span>
                                <button type="button" onClick={removeCoupon} className="text-xs text-[color:var(--maroon)] hover:underline">Remove</button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={couponInput}
                                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                                    placeholder="Coupon code"
                                    className="field flex-1 !py-2 text-sm"
                                    data-testid="coupon-input"
                                />
                                <button
                                    type="button"
                                    onClick={applyCoupon}
                                    disabled={applyingCoupon}
                                    className="btn-secondary !py-2 !px-4 text-xs shrink-0"
                                    data-testid="apply-coupon-button"
                                >
                                    {applyingCoupon ? "Checking…" : "Apply"}
                                </button>
                            </div>
                        )}
                        {couponError && <p className="text-xs text-[color:var(--maroon)] mt-1.5">{couponError}</p>}
                    </div>
                    <div className="mt-6 space-y-3 max-h-64 overflow-y-auto">
                        {cart.map((i) => (
                            <div key={`${i.product_id}-${i.size}`} className="flex gap-3 items-center">
                                <img src={i.image} alt={i.name} className="w-14 h-16 object-cover border border-[color:var(--border-gold)]" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-heading text-sm text-[color:var(--maroon)] truncate">{i.name}</p>
                                    <p className="font-ui text-[10px] tracking-widest-x uppercase text-[color:var(--ink-muted)]">Qty {i.quantity}{i.size ? ` · ${i.size}"` : ""}</p>
                                </div>
                                <span className="font-body text-sm text-[color:var(--gold)] font-semibold">{formatINR(i.price * i.quantity)}</span>
                                {!pendingOrder && (
                                    <button
                                        type="button"
                                        onClick={() => (wantsBuyNow ? clearBuyNow() : removeFromCart(i.product_id, i.size))}
                                        className="text-[color:var(--maroon)] hover:text-[color:var(--copper)] ml-1"
                                        aria-label={`Remove ${i.name}`}
                                        title="Remove this item"
                                        data-testid={`checkout-remove-${i.product_id}`}
                                    >
                                        <X size={15} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-[color:var(--border-gold)] mt-5 pt-5 space-y-2 font-body text-sm">
                        <div className="flex justify-between"><span className="text-[color:var(--ink-muted)]">Subtotal</span><span>{formatINR(cartSubtotal)}</span></div>
                        {discountAmount > 0 && (
                            <div className="flex justify-between text-[color:var(--emerald)]"><span>Discount ({couponApplied.code})</span><span>−{formatINR(discountAmount)}</span></div>
                        )}
                        <div className="flex justify-between"><span className="text-[color:var(--ink-muted)]">Shipping</span><span>{shipping === 0 ? <span className="text-[color:var(--emerald)]">Free</span> : formatINR(shipping)}</span></div>

                    </div>
                    <div className="border-t border-[color:var(--border-gold)] mt-3 pt-4 flex justify-between items-baseline">
                        <span className="font-heading text-lg text-[color:var(--maroon)]">Total</span>
                        <span className="font-heading text-2xl text-[color:var(--gold)] font-semibold">{formatINR(total)}</span>
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary w-full mt-6 justify-center" data-testid="place-order-button">
                        {loading ? "Please wait..." : pendingOrder ? "Retry Payment" : paymentMethod === "online" ? "Pay & Place Order" : "Place Order"}
                    </button>
                </aside>
            </form>
        </div>
    );
};

export default Checkout;
